import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Row, Col, Card, Button, Form, Badge, Alert, Pagination, Spinner } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { EventScannerNav } from '../../components/scanner';
import { apiService } from '../../services/api';
import { AttendeesResponse, Attendee } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import SkeletonLoader from '../../components/common/SkeletonLoader';
import './EventAttendeesPage.scss';

const EventAttendeesPage: React.FC = () => {
  const { eventSlug } = useParams<{ eventSlug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attendeesData, setAttendeesData] = useState<AttendeesResponse | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | 'scanned' | 'pending'>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [processing, setProcessing] = useState<string | null>(null);


  const fetchAttendees = useCallback(async () => {
    if (!eventSlug) return;

    try {
      setLoading(true);
      setError(null);
      const data = await apiService.scanner.getEventAttendees(eventSlug, {
        search: searchQuery,
        category: categoryFilter,
        status: statusFilter,
        page: currentPage,
      });
      setAttendeesData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load attendees');
    } finally {
      setLoading(false);
    }
  }, [eventSlug, searchQuery, categoryFilter, statusFilter, currentPage]);

  useEffect(() => {
    fetchAttendees();
  }, [fetchAttendees]);

  const handleScan = useCallback(async (passId: string) => {
    if (!eventSlug || !attendeesData) return;

    const previousData = attendeesData;
    const scannedBy = user?.email || user?.username || 'Unknown';

    // Optimistically update the local state
    setAttendeesData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        attendees: prev.attendees.map((attendee) =>
          attendee.id.toString() === passId
            ? {
                ...attendee,
                scanned: true,
                scanned_by: scannedBy,
                scanned_on: new Date().toISOString(),
              }
            : attendee
        ),
      };
    });

    try {
      setProcessing(passId);
      await apiService.scanner.scanAttendee(eventSlug, passId);
      // Success - state already updated
    } catch (err: any) {
      // Revert on error
      setAttendeesData(previousData);
      setError(err.message || 'Failed to scan attendee');
    } finally {
      setProcessing(null);
    }
  }, [eventSlug, attendeesData, user]);

  const handleUnscan = useCallback(async (passId: string) => {
    if (!eventSlug || !attendeesData) return;

    const previousData = attendeesData;

    // Optimistically update the local state
    setAttendeesData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        attendees: prev.attendees.map((attendee) =>
          attendee.id.toString() === passId
            ? {
                ...attendee,
                scanned: false,
                scanned_by: null,
                scanned_on: null,
              }
            : attendee
        ),
      };
    });

    try {
      setProcessing(passId);
      await apiService.scanner.unscanAttendee(eventSlug, passId);
      // Success - state already updated
    } catch (err: any) {
      // Revert on error
      setAttendeesData(previousData);
      setError(err.message || 'Failed to unscan attendee');
    } finally {
      setProcessing(null);
    }
  }, [eventSlug, attendeesData]);

  const handleResendEmail = useCallback(async (passId: string) => {
    if (!eventSlug) return;

    try {
      setProcessing(passId);
      await apiService.scanner.resendPassEmail(eventSlug, passId);
      alert('Email queued successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to resend email');
    } finally {
      setProcessing(null);
    }
  }, [eventSlug]);

  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
  }, []);

  const handleBackToDashboard = useCallback(() => {
    navigate('/scanner/');
  }, [navigate]);

  const paginationItems = useMemo(() => {
    if (!attendeesData || attendeesData.pagination.total_pages <= 1) return null;
    
    const pages = Array.from({ length: attendeesData.pagination.total_pages }, (_, i) => i + 1);
    return pages.filter(
      (page) =>
        page === 1 ||
        page === attendeesData.pagination.total_pages ||
        Math.abs(page - attendeesData.pagination.current_page) <= 2
    );
  }, [attendeesData]);

  if (loading && !attendeesData) {
    return (
      <Container className="event-attendees-page py-3 scanner-page-with-nav">
        <SkeletonLoader />
      </Container>
    );
  }

  return (
    <>
      <Container className="event-attendees-page py-3 scanner-page-with-nav">
        {/* Header */}
        <div className="scanner-header-wrapper mb-4">
          <div className="scanner-header">
            <button
              className="btn btn-glass btn-icon"
              onClick={handleBackToDashboard}
              title="Back to Dashboard"
            >
              <i className="fas fa-arrow-left"></i>
            </button>
            <div className="header-content">
              <div className="header-icon">
                <i className="fas fa-users"></i>
              </div>
              <div className="header-text">
                <h1 className="text-primary mb-2">Event Attendees</h1>
                <p className="text-muted mb-0">Manage and scan event attendees</p>
              </div>
            </div>

          </div>
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Card className="glass-card mb-3">
          <Card.Body className="p-3">
            <Form onSubmit={handleSearch}>
              <Row className="g-2">
                <Col xs={12} md={4}>
                  <Form.Group className="mb-2">
                    <Form.Label className="small">Search</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Name, email, phone, or identifier"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      size="sm"
                    />
                  </Form.Group>
                </Col>
                <Col xs={6} md={3}>
                  <Form.Group className="mb-2">
                    <Form.Label className="small">Category</Form.Label>
                    <Form.Select
                      value={categoryFilter}
                      onChange={(e) => {
                        setCategoryFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                      size="sm"
                    >
                      <option value="">All Categories</option>
                      {attendeesData?.categories.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={6} md={3}>
                  <Form.Group className="mb-2">
                    <Form.Label className="small">Status</Form.Label>
                    <Form.Select
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(e.target.value as '' | 'scanned' | 'pending');
                        setCurrentPage(1);
                      }}
                      size="sm"
                    >
                      <option value="">All</option>
                      <option value="scanned">Scanned</option>
                      <option value="pending">Pending</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} md={2} className="d-flex align-items-end">
                  <Button type="submit" variant="primary" className="w-100" size="sm">
                    <i className="fas fa-search me-2"></i>Search
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>

        {/* Attendees List */}
        <Card className="glass-card">
          <Card.Header className="p-3">
            <Card.Title className="card-title mb-0">
              <i className="fas fa-list me-2"></i>
              Attendees ({attendeesData?.pagination.total_count || 0})
            </Card.Title>
          </Card.Header>
          <Card.Body className="p-3">
            {loading ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" />
              </div>
            ) : attendeesData?.attendees.length === 0 ? (
              <div className="empty-state text-center py-5">
                <i className="fas fa-users fa-3x text-muted mb-3"></i>
                <p className="text-muted mb-0">No attendees found</p>
              </div>
            ) : (
              <>
                <div className="attendees-list">
                  {attendeesData?.attendees.map((attendee) => (
                    <div
                      key={attendee.id}
                      className={`attendee-card ${attendee.scanned ? 'scanned' : 'pending'}`}
                    >
                      <div className="attendee-header">
                        <div className="attendee-category">
                          <Badge bg="secondary" className="category-badge">
                            {attendee.category}
                          </Badge>
                          {attendee.seat_info && (
                            <span className="seat-info">
                              <i className="fas fa-chair me-1"></i>
                              {attendee.seat_info.number}
                            </span>
                          )}
                        </div>
                        <Badge
                          bg={attendee.scanned ? 'success' : 'warning'}
                          className="status-badge"
                        >
                          <i className={`fas ${attendee.scanned ? 'fa-check' : 'fa-clock'} me-1`}></i>
                          {attendee.scanned ? 'Scanned' : 'Pending'}
                        </Badge>
                      </div>
                      <div className="attendee-body">
                        <div className="attendee-info">
                          <h6 className="attendee-name">
                            <i className="fas fa-user me-2"></i>
                            {attendee.name}
                          </h6>
                          <p className="attendee-email">
                            <i className="fas fa-envelope me-2"></i>
                            {attendee.email}
                          </p>
                          {attendee.phone && (
                            <small className="attendee-phone">
                              <i className="fas fa-phone me-2"></i>
                              {attendee.phone}
                            </small>
                          )}
                          {attendee.scanned && attendee.scanned_on && (
                            <div className="scan-info mt-2">
                              <small className="text-muted">
                                <i className="fas fa-clock me-1"></i>
                                {new Date(attendee.scanned_on).toLocaleString()}
                                {attendee.scanned_by && (
                                  <>
                                    {' - '}
                                    <i className="fas fa-user-check me-1"></i>
                                    by {attendee.scanned_by}
                                  </>
                                )}
                              </small>
                            </div>
                          )}
                        </div>
                        <div className="attendee-actions">
                          {!attendee.scanned ? (
                            <>
                              <Button
                                size="sm"
                                variant="success"
                                onClick={() => handleScan(attendee.id.toString())}
                                disabled={processing === attendee.id.toString()}
                                className="action-btn"
                              >
                                {processing === attendee.id.toString() ? (
                                  <Spinner size="sm" animation="border" />
                                ) : (
                                  <>
                                    <i className="fas fa-check me-1"></i>Scan
                                  </>
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="info"
                                onClick={() => handleResendEmail(attendee.id.toString())}
                                disabled={processing === attendee.id.toString()}
                                className="action-btn"
                              >
                                {processing === attendee.id.toString() ? (
                                  <Spinner size="sm" animation="border" />
                                ) : (
                                  <>
                                    <i className="fas fa-envelope me-1"></i>Email
                                  </>
                                )}
                              </Button>
                            </>
                          ) : (
                            <>
                              {attendeesData.has_unscan_permission && (
                                <Button
                                  size="sm"
                                  variant="warning"
                                  onClick={() => handleUnscan(attendee.id.toString())}
                                  disabled={processing === attendee.id.toString()}
                                  className="action-btn"
                                >
                                  {processing === attendee.id.toString() ? (
                                    <Spinner size="sm" animation="border" />
                                  ) : (
                                    <>
                                      <i className="fas fa-undo me-1"></i>Unscan
                                    </>
                                  )}
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {attendeesData && attendeesData.pagination.total_pages > 1 && (
                  <div className="d-flex justify-content-center mt-3">
                    <Pagination size="sm">
                      <Pagination.First
                        onClick={() => setCurrentPage(1)}
                        disabled={!attendeesData.pagination.has_previous}
                      />
                      <Pagination.Prev
                        onClick={() => setCurrentPage((p) => p - 1)}
                        disabled={!attendeesData.pagination.has_previous}
                      />
                      {paginationItems?.map((page, index, array) => (
                        <React.Fragment key={page}>
                          {index > 0 && array[index - 1] !== page - 1 && <Pagination.Ellipsis />}
                          <Pagination.Item
                            active={page === attendeesData.pagination.current_page}
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </Pagination.Item>
                        </React.Fragment>
                      ))}
                      <Pagination.Next
                        onClick={() => setCurrentPage((p) => p + 1)}
                        disabled={!attendeesData.pagination.has_next}
                      />
                      <Pagination.Last
                        onClick={() => setCurrentPage(attendeesData.pagination.total_pages)}
                        disabled={!attendeesData.pagination.has_next}
                      />
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </Card.Body>
        </Card>
      </Container>
      <EventScannerNav />
    </>
  );
};

export default EventAttendeesPage;

